import { Credentials } from '@/types/auth.types';
import { configService } from './configService';
import { debug } from '@/config/constants';
import { SignatureV4 } from '@aws-sdk/signature-v4';
import { Sha256 } from '@aws-crypto/sha256-js';
import { HttpRequest } from '@aws-sdk/protocol-http';

type MetricType = 'CALL_CONNECTED' | 'CALL_ENDED' | 'QUEUE_PROMOTED' | 'QUEUE_ABANDONED';

interface RecordMetricParams {
  metricType: MetricType;
  sessionId: string;
  contactId?: string;
  queueId?: string;
  featureName?: string;
  waitTime?: number; // seconds waited in queue
  callDuration?: number; // seconds on call
}

class MetricsService {
  /**
   * Record a metric event
   */
  async recordMetric(
    credentials: Credentials,
    params: RecordMetricParams
  ): Promise<void> {
    try {
      const config = configService.getConfig();
      if (!config) {
        debug.warn('Config not loaded, skipping metric');
        return;
      }

      const url = new URL(`${config.API_ENDPOINT}metrics/record`);
      
      const request = new HttpRequest({
        method: 'POST',
        protocol: url.protocol,
        hostname: url.hostname,
        path: url.pathname,
        headers: {
          'Content-Type': 'application/json',
          'host': url.hostname,
        },
        body: JSON.stringify(params),
      });

      const signer = new SignatureV4({
        credentials: {
          accessKeyId: credentials.accessKeyId,
          secretAccessKey: credentials.secretAccessKey,
          sessionToken: credentials.sessionToken,
        },
        region: config.REGION,
        service: 'execute-api',
        sha256: Sha256,
      });

      const signedRequest = await signer.sign(request);

      const response = await fetch(`${url.protocol}//${url.hostname}${url.pathname}`, {
        method: signedRequest.method,
        headers: signedRequest.headers as HeadersInit,
        body: signedRequest.body,
      });

      if (!response.ok) {
        debug.warn('Failed to record metric:', response.statusText);
      } else {
        debug.log(`✓ Metric recorded: ${params.metricType}`);
      }
    } catch (error) {
      console.error('Error recording metric:', error);
      // Don't throw - metrics are best-effort
    }
  }
}

export const metricsService = new MetricsService();
